/**
 * 分享图片上传：把单据 PNG 上传到同步服务器，返回可直接访问的图片 URL。
 * 他人打开该 URL 即可在线查看送货单图片，无需下载文件。
 */

import { loadSession } from './authApi';
import { getSyncConfig } from './syncConfig';
import { buildSyncHeaders } from './syncApi';

const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface ShareImageResult {
  url: string;
  expiresAt: number;
}

/**
 * 将 base64 PNG 图片上传到服务器 share-images 目录，返回公开可访问的图片 URL。
 * @param dataUrl PNG base64 data URL（image/png）
 * @param name 业务标识（如销售单号），仅用于文件名前缀
 */
export async function uploadShareImage(
  dataUrl: string,
  name?: string
): Promise<ShareImageResult> {
  if (!dataUrl.startsWith('data:image/png;base64,')) {
    throw new Error('仅支持 PNG 格式图片上传');
  }
  const serverUrl = getSyncConfig()?.serverUrl || window.location.origin;
  const token = loadSession()?.token || '';
  const base = serverUrl.replace(/\/$/, '');
  const safeName = (name || 'share').replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) || 'share';
  const resp = await fetch(`${base}/api/share-image`, {
    method: 'POST',
    headers: buildSyncHeaders(token),
    body: JSON.stringify({
      image: dataUrl,
      name: safeName,
    }),
  });
  const data = (await resp.json()) as { url?: string; error?: string };
  if (!resp.ok || !data.url) {
    throw new Error(data.error || `上传失败 (${resp.status})`);
  }
  // 若服务器返回相对路径，拼接当前 origin；若是完整 URL 则直接使用
  const url = data.url.startsWith('http')
    ? data.url
    : `${window.location.origin}${data.url.startsWith('/') ? '' : '/'}${data.url}`;
  // URL 序列化会将旧服务端可能返回的中文文件名编码，便于在微信/QQ 中转发。
  return { url: new URL(url).href, expiresAt: Date.now() + DEFAULT_TTL_MS };
}
